import { Component, OnInit, Output, EventEmitter, Input, ViewChild, ElementRef} from '@angular/core';
import { SettingsService } from '../../../shared/api/resources/settings/settings.service';
import { Customer } from '../../../shared/api/model/customer';
import { GenderPrice } from '../../../shared/api/model/gender-price';
import { StaticUtils } from '../../../shared/utils/static-utils';
import { Event } from '../../../shared/api/model/event';
import { debounce } from 'lodash';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';

@Component({
  selector: 'app-book-details',
  templateUrl: './book-details.component.html',
  styleUrls: ['./book-details.component.scss'],
  providers: []
})
export class BookDetailsComponent implements OnInit {
  @Input('genderPrices') genderPrices: GenderPrice[] = [];
  @Input('customer') customer = new Customer();
  @Input('event') event = new Event();
  @Input() calculatingPrice: boolean;
  @Output() onGenderPricesChange = new EventEmitter<any>();
  @Output() onCustomerChange = new EventEmitter<any>();
  @Output() onIsValidChange = new EventEmitter<any>();
  @Output() onBookingAnswerChange = new EventEmitter<any>();
  @Output() _reachedMaxPaxBook = new EventEmitter<any>();
  @Output() _reachedMinPaxBook = new EventEmitter<any>();
  @Output() _reachedMinPaxEvent = new EventEmitter<any>();
  @Output() hasGenderPriceDepsActive = new EventEmitter<any>();
  @ViewChild('phoneInput') phoneInput: ElementRef;
  
  private _isFormValid = false;
  _sumOfGenderPriceQuantityToBook: number;
  isToShowMessageMinReached: boolean;
  isToShowMessageMaxReached: boolean;
  utils = StaticUtils;
  nationalities:any = [];
  notes = '';
  eventGenderPrices: GenderPrice[];
  countryCode2: string | null = null;
  onDialInput: () => void;

  constructor(private _settings: SettingsService) { }

  ngOnInit() {
    this.eventGenderPrices = this.genderPrices;
    this.customer.sendEmailCopy = true;
    this.updateCountryData();
    this.restoreCountryFromCustomer();
    this.checkValidity();

    this.onDialInput = debounce(() => {
      this.updateCountryByDial();
    }, 1000);
  }
  public updateCountryData(): void {
    this.nationalities = [];
    const topCodes = ['PT', 'GB', 'US', 'ES', 'FR'];
    const topCountries = this._settings.countries.filter(c => topCodes.includes(c.code2)).map(country => {
      return {
        label: country.name,
        value: country
      }
    });
    const allCountries = this._settings.countries.map(country => {
      return {
        label: country.name,
        value: country
      }
    });

    this.nationalities = [
        {
            groupLabel: 'Top 5 - Countries',
            items: topCountries
        },
        {
            groupLabel: 'All Countries',
            items: allCountries
        }
    ];
  }

  updateCountryByDial() {
  const raw = this.normalizeDial(this.customer.dial);

  const country = this.findCountryByDial(raw);

  if (country) {
    this.applyCountryInfo(country);
  } else {
    this.resetCountryInfo();
  }
  }

  onCountrySelect(country: any) {
    this.applyCountryInfo(country);
    this.triggerCustomerChange();
  }

    private findCountryByDial(dialInput: string): any | null {
    const dial = this.normalizeDial(dialInput);
    if (!dial) return null;

    const flatItems = this.getFlatCountries();
    const country: any = flatItems.find((n: any) => {
      const nDial = this.normalizeDial(n.dial);
      return nDial === dial;
    });

    return country || null;
  }

  private applyCountryInfo(country: any) {
    // Single place to keep component and customer country info in-sync
    this.customer.country = country.id;
    this.customer.dial = country.code;
    this.customer.countryName = country.name;
    this.countryCode2 = country.code2;
  }

  private resetCountryInfo() {
    this.customer.country = null;
    this.customer.dial = null;
    this.customer.countryName = '';
    this.countryCode2 = null;
  }

  /**
   * Restore internal country state (countryCode2) from the
   * current customer object (customer.country or customer.dial).
   * This ensures validation using libphonenumber-js has the ISO2 code available
   * when the parent replaces the customer (for example when switching tabs).
   */
  private restoreCountryFromCustomer() {
    if (!this.customer) return;

    const flatItems = this.getFlatCountries();
    let country: any | undefined;

    // Prefer explicit customer.country id
    if (this.customer.country) {
      country = flatItems.find((n: any) => n.id === this.customer.country);
    }

    // Fallback to dial/code lookup if country id not found
    if (!country && this.customer.dial) {
      const dial = this.normalizeDial(this.customer.dial);
      country = flatItems.find((n: any) => {
        return this.normalizeDial(n.dial) === dial || this.normalizeDial(n.code) === dial;
      });
    }

    if (country) {
      this.applyCountryInfo(country);
    }
  }

  /**
   * Return a flat array of country objects from `nationalities`.
   */
  private getFlatCountries(): any[] {
    return (this.nationalities || []).reduce((acc: any, cur: any) => {
      acc.push(...(cur.items || []).map((item: any) => item.value));
      return acc;
    }, []);
  }

  /**
   * Normalize a dial/code input to only digits and strip leading "00".Returns '' for falsy inputs.
   */
  private normalizeDial(val: any): string {
    if (!val && val !== 0) return '';
    let s = ('' + val).replace(/\D/g, '');
    if (s.startsWith('00')) s = s.slice(2);
    return s;
  }

  public reachedMaximumPaxAndIsBlocking = (maximumPaxPerBooking: number, rejectBookingBasedOnMinMax:boolean, eventGenderPrices: any): boolean => {
    this._sumOfGenderPriceQuantityToBook = this.sumQuantities(eventGenderPrices);

    if (!rejectBookingBasedOnMinMax) {
      return false;
    }
    
    if (maximumPaxPerBooking !== 0 && maximumPaxPerBooking === this._sumOfGenderPriceQuantityToBook) {
        return true;
    }
    
    return false;
  }

  public genderPricesDependenciesMessage(genderPrices: any) {
    let validationMessages: Array<string> = [];
    
    if (!genderPrices) {
        return [];
    }

    genderPrices.forEach((price: {quantityToBook: number; genderPriceDependencies: any[]; audienceTypeDescription: any; dependencySettings: any}) => {
        if (price.quantityToBook <= 0) {
            return;
        }

        if (!price.genderPriceDependencies || price.genderPriceDependencies.length === 0) {
            return;
        }

        const dependencyType = price.dependencySettings && price.dependencySettings.dependencyType;
        const deps = price.genderPriceDependencies;

        // Dependency Type | 1 - OR | 0 - AND
        if (dependencyType === 1) {
            const hasSomeDependentPrice = deps.some(dep => {
                    const dependentPrice = genderPrices.find((p: {
                        audienceType: any;
                        audienceTypeGroup: any;
                    }) =>
                        p.audienceType === dep.genderPriceDependsOn.audienceType &&
                        p.audienceTypeGroup === dep.genderPriceDependsOn.audienceTypeGroup
                    );

                    return dependentPrice && dependentPrice.quantityToBook >= dep.quantity;
                }
            );

            if (!hasSomeDependentPrice) {
                const allDepsJoined = this.buildDepsList(deps, ' OR ', genderPrices);
                const errorMessage = `The ${price.audienceTypeDescription} requires at least ${allDepsJoined} in the reservation.`;

                validationMessages.push(errorMessage);
            }
        } else {
            // Filters unmet dependencies
            const unmetDeps = deps.filter(dep => {
              const dependentPrice = genderPrices.find(p =>
                p.audienceType === dep.genderPriceDependsOn.audienceType &&
                p.audienceTypeGroup === dep.genderPriceDependsOn.audienceTypeGroup
              );
              
              return !dependentPrice || dependentPrice.quantityToBook < dep.quantity;
            });

            if (unmetDeps.length > 0) {
              const allDepsJoined = this.buildDepsList(unmetDeps, ' AND ', genderPrices);
              const errorMessage = `The ${price.audienceTypeDescription} requires at least ${allDepsJoined} in the reservation.`;

              validationMessages.push(errorMessage);
            }
        }
    });

    this.hasGenderPriceDepsActive.emit(validationMessages.length > 0);

    return validationMessages;
  }

  buildDepsList(deps: any, operator: string, genderPrices: any) {
      if (!Array.isArray(deps)) {
          return [];
      }

      // For each dependency, generate only the part: '1 ADULT' and joined them
      return deps.map(dep => {
          const dependentDescription = this.getAudienceTypeDescription(
              genderPrices,
              dep.genderPriceDependsOn.audienceType,
              dep.genderPriceDependsOn.audienceTypeGroup
          );

          return `${dep.quantity} ${dependentDescription}`;
      }).join(operator);
  }

  // Find the description corresponding to audienceType
  getAudienceTypeDescription(genderPrices: any[], audienceType: any, audienceTypeGroup: any) {
      const price = genderPrices.find((p: { audienceType: any; audienceTypeGroup: any; }) => p.audienceType === audienceType && p.audienceTypeGroup === audienceTypeGroup);

      return price ? price.audienceTypeDescription : "Unknown Group";
  }

  public reachedMaxPaxBook(event: Event, eventGenderPrices:any): boolean {
    let maximumPaxPerBooking = event.maximumPaxPerBooking
    let rejectBookingBasedOnMinMax = event.rejectBookingBasedOnMinMax
    this._sumOfGenderPriceQuantityToBook = this.sumQuantities(eventGenderPrices);

    if (rejectBookingBasedOnMinMax) {
      if (maximumPaxPerBooking === this._sumOfGenderPriceQuantityToBook && maximumPaxPerBooking !== 0) {
        this.isToShowMessageMaxReached = true
        this._reachedMaxPaxBook.emit(this.isToShowMessageMaxReached)
      } else {
        this.isToShowMessageMaxReached = false
        this._reachedMaxPaxBook.emit(this.isToShowMessageMaxReached)
      }
    } else if (maximumPaxPerBooking < this._sumOfGenderPriceQuantityToBook && maximumPaxPerBooking !== 0) {
        this.isToShowMessageMaxReached = true
        this._reachedMaxPaxBook.emit(this.isToShowMessageMaxReached)
    } else {
      this.isToShowMessageMaxReached = false
      this._reachedMaxPaxBook.emit(this.isToShowMessageMaxReached)
    }

    return this.isToShowMessageMaxReached    
  }

  public reachedMinPaxBook(event: Event, eventGenderPrices:any): boolean {
    let minimumPaxPerBooking = event.minimumPaxPerBooking
    this._sumOfGenderPriceQuantityToBook = this.sumQuantities(eventGenderPrices);

    if (minimumPaxPerBooking > this._sumOfGenderPriceQuantityToBook && minimumPaxPerBooking !== 0) {
      this.isToShowMessageMinReached = true
      this._reachedMinPaxBook.emit(this.isToShowMessageMinReached)
    } else {
      this.isToShowMessageMinReached = false
      this._reachedMinPaxBook.emit(this.isToShowMessageMinReached)
    }

    return this.isToShowMessageMinReached    
  }

  public reachedMinPaxEvent(event: Event, eventGenderPrices:any): boolean {
    let minimumLotation = event.minimumLotation
    this._sumOfGenderPriceQuantityToBook = this.sumQuantities(eventGenderPrices);

    if (minimumLotation > (this._sumOfGenderPriceQuantityToBook + event.quantityBooked) && minimumLotation !== 0) {
      this.isToShowMessageMinReached = true
      this._reachedMinPaxEvent.emit(this.isToShowMessageMinReached)
    } else {
      this.isToShowMessageMinReached = false
      this._reachedMinPaxEvent.emit(this.isToShowMessageMinReached)
    }

    return this.isToShowMessageMinReached    
  }

  public hasReachedEventQuantityAvailable(event: Event): boolean {
    // true when there is nothing left (<= 0)
    return this.getMax(event) <= 0;
  }

  public getMax(event: Event): number {
    const reservationQuantityToBook = event.eventGenderPrices
      .filter((egp) => egp.audienceType !== null)
      .reduce((acc, cur) => {
        return acc += cur.quantityToBook;
      }, 0);

    return event.quantityAvailable - reservationQuantityToBook;
  }

  public isUnlimited(event: Event): boolean {
    return event.availabilityType === 1
      || event.productAvailabilityTypeDescription === 'Unlimited'
      || event.hasLotation === false
      || event.quantityAvailable === -1;
  }
    
  get totalQuantity(): number {
    let result = 0;

    for (const eventGenderPrice of this.eventGenderPrices) {
      if (this.isGenderPriceVisible(eventGenderPrice)) {

        if(eventGenderPrice.audienceTypeOccupancy >= 2) {
          result += eventGenderPrice.audienceTypeOccupancy * eventGenderPrice.quantityToBook;
        }
        else {
          result += eventGenderPrice.quantityToBook;
        }

      }
    }

    return result;
  }

  public isGenderPriceVisible(genderPrice: GenderPrice): boolean {
    return genderPrice.genderPriceType !== 1 || (genderPrice.genderPriceType === 1 && genderPrice.isExcludedFromBilling);
  }

  countryString(val: string) {
    const flatItems: any[] = this.nationalities.reduce((acc, cur) => {
      acc.push(...cur.items.map(item => item.value));

      return acc;
    }, []);

    // Filter country
    const country: any = flatItems.find((n: any) => n.id === val);
    
    return country.name;
  }

  getRemainingSlots(idx: number): number {
    const available = this.event.quantityAvailable;

    if (available === -1) {
      return Infinity;
    }

    const totalBookedExceptCurrent = this.genderPrices
      .filter((ticket, i) => i !== idx && ticket.audienceType != null)
      .reduce((sum, gp) => sum + gp.quantityToBook, 0);
      
    return this.event.quantityAvailable - totalBookedExceptCurrent;
  }

  updatePrice(index: number, amount: number) {
    const maxForThis = this.getRemainingSlots(index);
    this.genderPrices[index].quantityToBook = Math.min(amount, maxForThis);

    this.triggerGenderPricesChange();
  }

  triggerGenderPricesChange() {
    this.onGenderPricesChange.emit({
      genderPrices: this.genderPrices,
    });
  }

  triggerCustomerChange() {
    this.onCustomerChange.emit({
      customer:  this.customer,
      notes: this.notes
    });

    this.checkValidity();
  }

  triggerBookinganswerChange() {
    this.onBookingAnswerChange.emit({
      bookingAnswer: this.customer.bookingAnswer,
    });
  }

  public getPriceGroupOldPrice(genderPrice: GenderPrice): boolean | number {
    if (genderPrice.genderPriceType !== 3 || 
        !this.genderPrices ||
        !this.event.passengerSettings || 
        (!this.event.passengerSettings.configurations && !this.event.passengerSettings.tieredConfigurations) ||
        genderPrice.genderPriceType === 3 && this.event.passengerSettings.format === 0) {
        
        return false;
    }

    const updatedPricePerson: any = this.genderPrices.find(b => b.audienceType === genderPrice.audienceType);
    const prices = this.event.passengerSettings.format === 2 ? this.event.passengerSettings.tieredConfigurations[updatedPricePerson.hashedAudience] : this.event.passengerSettings.configurations;
    const indexActualPrice = prices.findIndex(a => a.price === updatedPricePerson.grossprice);
    let result: any;
    
    if (indexActualPrice === 0) {
        return false;
    }

    const index = indexActualPrice === -1 ? prices.length - 1 : indexActualPrice - 1;
    result = prices[index].price;
    
    return result;
  }

  get isToShowPerGroupPrices(): boolean {
      const passengerSettings = this.event && this.event.passengerSettings;
      const bookingGenderPrices = this.genderPrices || [];

      if (!passengerSettings) {
          return false;
      }

      const isCorrectFormat = passengerSettings.format === 1 || passengerSettings.format === 2;
      const hasQuantityToBook = bookingGenderPrices.some(price => price.quantityToBook > 0);

      return isCorrectFormat && hasQuantityToBook;
  }

  get hasMoreThanOnePricePerGroup(): boolean {
    const passengerSettings = this.event.passengerSettings;
    const eventGenderPrices = this.eventGenderPrices;
  
    if (!passengerSettings || !eventGenderPrices) return false;
  
    const hasConfig = passengerSettings.configurations && passengerSettings.configurations.length > 1;
    const hasTiered = passengerSettings.tieredConfigurations && Object.values(passengerSettings.tieredConfigurations).some(arr => arr && arr.length > 1);
    const isPricePerGroup = eventGenderPrices.some(p => p.genderPriceType === 3);
  
    return (hasConfig || hasTiered) && isPricePerGroup;
  } 


  public getGenderName(genderPrice: GenderPrice): string {
    return genderPrice.audienceTypeDescription || 'Tickets';
  }

  public getGenderPriceLabel(genderPrice: GenderPrice): string {
    return genderPrice.audienceTypeLabel || '';
  }

  getSplitPriceValue(genderPrice: GenderPrice): number {
    let result = 0;
    if (genderPrice.genderPriceType === 2) {
      for (const genderPriceComponent of genderPrice.productGenderPriceComponents) {
        result += genderPriceComponent.selectedPrice.value;
      }
    }
    return result;
  }

  checkValidity() {
    if ( !this.isNameEmpty() && !this.isCountryEmpty() && !this.isPhoneEmpty() && this.isPhoneValid() && !this.isEmailEmpty() && this.isEmailValid() && !this.isDialEmpty() &&
       (!this.event.bookingQuestion || !this.event.bookingQuestionIsMandatory || !this.event.bookingQuestionIsActive || !this.isBookingAnswerEmpty())) {
      this.isFormValid = true;
    } else {
      this.isFormValid = false;
    }
  }

  isNameEmpty(): boolean {
    return !this.customer.name || this.customer.name === '';
  }

  onNameBlur() {
    this.checkValidity();
  }

  isCountryEmpty() {
    return this.customer.country === undefined || this.customer.country === null;
  }

  onCountryBlur() {
    this.checkValidity();
  }

  isPhoneEmpty() {
    return !this.customer.phone || this.customer.phone === '';
  }

  isDialEmpty() {
    return !this.customer.dial || this.customer.dial === '';
  }

  isPhoneValid(): boolean {
    // If there is no country defined, it is invalid
    if (!this.countryCode2) {
      return false;
    }

    // Normalize the country to uppercase and make the parse
    const iso2 = this.countryCode2.toUpperCase() as CountryCode;
    const phone = (this.customer.phone || '').trim();
    const phoneNumber = parsePhoneNumberFromString(phone, iso2);

    return phoneNumber ? phoneNumber.isValid() : false;
  }

  onPhoneBlur() {
    this.checkValidity();
  }

  onEmailBlur() {
    this.checkValidity();
  }

  isEmailEmpty(): boolean {
    return !this.customer.email || this.customer.email === '';
  }

  isEmailValid() {
    const re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    return re.test(this.customer.email);
  }

  isBookingAnswerEmpty(): boolean {
    return !this.customer.bookingAnswer || this.customer.bookingAnswer === '';
  }

  onBookingAnswerBlur() {
    this.checkValidity();
  }

  get isFormValid(): boolean {
    return this._isFormValid;
  }

  set isFormValid(value: boolean) {
    if (this._isFormValid !== value) {
      this._isFormValid = value;
      this.onIsValidChange.emit(this._isFormValid);
    }
  }

  private sumQuantities(eventGenderPrices: any[]): number {
    return eventGenderPrices
      .filter((gp: any) => gp.audienceType !== null)
      .map((gp: any) => gp.quantityToBook)
      .reduce((prev: any, next: any) => prev + next, 0);
  }
}



// WEBPACK FOOTER //
// ./src/app/experiences/booking/book-details/book-details.component.ts